import { zWeightUnit } from '@strength/domain';
import { getMetricSpec, getValueFromData, roundDelta } from '.';
import { applyBenchmark } from '../benchmark';
import { z } from 'zod';
import { tKey } from '@strength/i18n';
/**
 * Determines the difference between a metric prescribed value and the actual performed value.
 * This takes into account the metric type and the discrepancies between each type.
 *
 * @param metricType type of metric to calculate delta for or the config of a custom metric
 * @param prescribed the raw string prescribed value
 * @param actual the raw string actual value
 * @param benchmarkData the libraryExerciseId for the current exercise and existing benchmark data for it. Only used for intensity metrics.
 * @returns the difference between the prescribed and actual values, or zero if a difference cannot be calculated
 */
export function calculateDelta(metric, prescribed, actual, benchmarkData) {
    const { type, config } = metric;
    // Can't calculate delta with no prescription
    // or if the type is tempo
    // or if the value is a keyword
    if (type === 'tempo')
        return 0;
    if (!prescribed || config?.keywords.includes(prescribed))
        return 0;
    const spec = getMetricSpec(metric);
    const prescribedParsed = spec.schema.safeParse(prescribed);
    const actualParsed = spec.schema.safeParse(actual);
    if (!prescribedParsed.success || !actualParsed.success) {
        // We couldn't parse one of the values so we can't compare them
        return 0;
    }
    // We don't have actuals data, so nothing to compare to
    if (actualParsed.data === '')
        return 0;
    // Resolve to comparable absolute values before diffing. For intensity we apply
    // the 1RM so we compare weights (what the actual is recorded in). For a
    // KPI-mapped column we resolve the prescribed `%` to the athlete's target
    // (SC-4041), so we compare in the KPI unit. A non-`%` value or an unmapped
    // column passes through unchanged — so the actual (recorded in the KPI unit,
    // never a `%`) is a no-op through the KPI resolver.
    const prescribedData = benchmarkData
        ? applyBenchmark(prescribedParsed.data, {
            metricType: type,
            metricId: benchmarkData.metricId,
            benchmarkGridId: benchmarkData.benchmarkGridId,
            libraryExerciseId: benchmarkData.libraryExerciseId,
        }, benchmarkData.applyBenchmarkData, benchmarkData.profileId)
        : prescribedParsed.data;
    // We don't allow entering %'s in tablet/mobile, but this is here for posterity
    const actualData = benchmarkData
        ? applyBenchmark(actualParsed.data, {
            metricType: type,
            metricId: benchmarkData.metricId,
            benchmarkGridId: benchmarkData.benchmarkGridId,
            libraryExerciseId: benchmarkData.libraryExerciseId,
        }, benchmarkData.applyBenchmarkData, benchmarkData.profileId)
        : actualParsed.data;
    // If we couldn't convert an intensity to a weight, then we can't compare
    if (type === 'intensity' && prescribedData.format === 'percentage') {
        return 0;
    }
    // We don't try to compare 2 different units of measure
    if ((actualData.unit ?? '') !== (prescribedData.unit ?? '')) {
        return 0;
    }
    const actualNumber = getValueFromData(actualData);
    // If the actual value is somehow a range or keyword, then we won't try to compare
    if (typeof actualNumber === 'string')
        return 0;
    if (prescribedData.type === 'range') {
        const { min, max } = prescribedData;
        if (max == null) {
            return roundDelta(actualNumber - min);
        }
        if (actualNumber > max)
            return roundDelta(actualNumber - max);
        if (actualNumber < min)
            return roundDelta(actualNumber - min);
        return 0;
    }
    const prescribedNumber = getValueFromData(prescribedData);
    // This should never happen based on the previous checks, but it's a last resort
    if (typeof prescribedNumber === 'string')
        return 0;
    // Do a normal comparison
    return roundDelta(actualNumber - prescribedNumber);
}
/**
 * Orients a raw signed delta (from {@link calculateDelta}) so a positive result
 * always means "better" regardless of the metric's direction. This keeps the
 * actuals color test uniform: `orientedDelta > 0 === "better"`.
 *
 * For a normal metric (higher is better) the raw `actual - prescribed` already
 * points the right way, so the delta passes through unchanged. For an
 * inverse-scale metric (lower is better, e.g. sprint time) a negative raw delta
 * (actual below prescribed = good) must orient to positive, so we negate it.
 *
 * This is ONLY about delta orientation. It does not touch the displayed numeric
 * label ({@link calculateDelta} still returns the raw signed value) and it is
 * distinct from the prescription math in `applyKpiScale` (SC-3837 Task 2).
 *
 * @param delta the raw signed delta (`actual - prescribed`)
 * @param isInverseScale true when a lower value is better for this metric
 * @returns `delta` unchanged when normal, `-delta` when inverse-scale
 */
export function applyInverseScale(delta, isInverseScale) {
    return isInverseScale ? -delta : delta;
}
/**
 * Get the text needed to display a metric value in a text field
 *
 * @param metricType type of metric we are displaying or the configuration for a custom metric
 * @param prescribed the raw string prescribed value
 * @param actual the raw string actual value
 * @param benchmarkData the libraryExerciseId for the current exercise, an array of benchmarks (including the fromLibraryExerciseId benchmark of the conversion), and an array of max conversions. Only used for intensity metrics.
 * @returns the value of the input, the placeholder text, and the unit (if any)
 */
export function getInputDisplay({ metric, prescribedValue, actualValue, benchmarkData, defaultWeightUnit = zWeightUnit.options[0], }) {
    const { type } = metric;
    const spec = getMetricSpec(metric);
    const actualsParsed = spec.schema.safeParse(actualValue ?? '');
    const prescribedParsed = spec.schema.safeParse(prescribedValue ?? '');
    // Force the values to be valid metric shapes (empty string is valid)
    const actualsData = actualsParsed.success ? actualsParsed.data : '';
    const prescribedData = prescribedParsed.success ? prescribedParsed.data : '';
    const prescribedParseError = prescribedParsed.error;
    const actualParseError = actualsParsed.error;
    // Both are empty; nothing to display
    if (actualsData === '' && prescribedData === '') {
        return {
            value: '',
            placeholder: '',
            unit: type === 'intensity' ? defaultWeightUnit : undefined,
            prescribedParseError,
            actualParseError,
        };
    }
    // Tempo is just rendered as-is
    if (type === 'tempo') {
        return {
            value: prescribedValue ?? '',
            placeholder: '',
            unit: undefined,
            prescribedParseError,
            actualParseError,
        };
    }
    // Intensity has some special conditions due to the percentage
    if (type === 'intensity') {
        let placeholder = '';
        // `string` (not `WeightUnit`) because a resolved KPI intensity carries a KPI
        // unit (bpm, m/s, ...); InputDisplay.unit is a plain string anyway.
        let unit = defaultWeightUnit;
        // Build the placeholder using the prescription. A keyword prescription (e.g.
        // "Max") has no format/unit/range shape, so it's rendered as-is, with no unit
        // — matching how the generic fallback branch below treats a keyword's absent
        // `.unit` for Reps/custom.
        if (typeof prescribedData === 'object' &&
            prescribedData.type === 'keyword') {
            placeholder = prescribedData.value;
            unit = undefined;
        }
        else if (typeof prescribedData === 'object') {
            // Attempt to convert to a weight using the max
            let data = prescribedData.format === 'percentage' && benchmarkData
                ? applyBenchmark(prescribedData, {
                    metricType: type,
                    metricId: benchmarkData.metricId,
                    benchmarkGridId: benchmarkData.benchmarkGridId,
                    libraryExerciseId: benchmarkData.libraryExerciseId,
                }, benchmarkData.applyBenchmarkData, benchmarkData.profileId)
                : prescribedData;
            if (data === '') {
                // Couldn't convert; use the original data
                data = prescribedData;
            }
            // We don't allow selecting % in the UI, so we just set the unit to the default
            // This may get overridden below if the actual entered has a different unit
            unit = data.unit === '%' ? defaultWeightUnit : data.unit;
            const percent = data.format === 'percentage' ? '%' : '';
            if (data.type === 'range') {
                // Render low-to-high; an inverse-scale KPI range resolves each endpoint
                // independently and can yield min > max (see toDisplay/getValueFromData).
                const lo = Math.min(data.min, data.max);
                const hi = Math.max(data.min, data.max);
                placeholder = `${lo}-${hi}${percent}`;
            }
            else {
                placeholder = `${data.value}${percent}`;
            }
        }
        // Build the value using the actuals data if it exists (should be a normal number,
        // or a keyword — the desktop actuals grid has no numeric gate, unlike tablet/mobile,
        // so a keyword actual is a real recorded value here, not a hypothetical). Otherwise,
        // use the prescribed data only if it's a normal number (i.e. not a range, keyword,
        // percent) — a keyword only present as the prescription stays out of `value`,
        // matching how a prescribed range isn't shown as value either.
        let value = '';
        if (typeof actualsData === 'object' && actualsData.type === 'keyword') {
            value = actualsData.value;
            // A keyword actual has no unit either — clears whatever the prescription
            // branch set, so a numeric prescription + keyword actual (e.g. "80%" / "Max")
            // doesn't leak a stray unit next to the keyword.
            unit = undefined;
        }
        const data = typeof actualsData === 'object' ? actualsData : prescribedData;
        if (typeof data === 'object' && data.type === 'exact') {
            if (data.format === 'weight') {
                value = data.value;
                unit = data.unit;
            }
            else if (benchmarkData) {
                // Try to convert to a weight using the max. Only a `weight` result is
                // used here: an `intensity`-type metric is KPI-ineligible, so this never
                // resolves to a `kpi` result at runtime, and checking `format === 'weight'`
                // (rather than `unit !== '%'`) lets the type narrow to a WeightUnit.
                // `column.metricType: 'intensity'` guarantees the dispatcher's intensity
                // branch, which always returns `Intensity` — narrower than its declared
                // `NumericMetric` return type.
                const appliedBenchmark = applyBenchmark(data, {
                    metricType: type,
                    metricId: benchmarkData.metricId,
                    benchmarkGridId: benchmarkData.benchmarkGridId,
                    libraryExerciseId: benchmarkData.libraryExerciseId,
                }, benchmarkData.applyBenchmarkData, benchmarkData.profileId);
                if (appliedBenchmark !== '' &&
                    appliedBenchmark.type === 'exact' &&
                    appliedBenchmark.format === 'weight') {
                    // Successfully converted to a weight
                    value = appliedBenchmark.value;
                    unit = appliedBenchmark.unit;
                }
            }
        }
        return {
            value,
            placeholder,
            unit,
            prescribedParseError,
            actualParseError,
        };
    }
    if (prescribedData.type === 'complex') {
        const prescribedDelimiter = prescribedData.delimiter;
        const actualDelimiter = actualsData.delimiter ?? prescribedDelimiter;
        const placeholderDSV = prescribedData.reps?.join(prescribedDelimiter) ?? '';
        const valueDSV = actualsData.reps?.join(actualDelimiter) ?? placeholderDSV;
        const delimitersMatch = actualsData.delimiter === undefined ||
            actualsData.delimiter === prescribedDelimiter;
        return {
            value: valueDSV,
            placeholder: placeholderDSV,
            delimiter: actualDelimiter,
            unit: undefined,
            prescribedParseError,
            actualParseError: delimitersMatch
                ? actualParseError
                : new z.ZodError([
                    {
                        code: 'custom',
                        message: tKey('delimiterMismatch')[0],
                        path: [],
                    },
                ]),
        };
    }
    // Handle all other metric types. A KPI-mapped column resolves an entered `%`
    // to the athlete's per-athlete target (SC-3844); a non-`%` value (e.g. the
    // recorded actual in its KPI unit), an unmapped column, or the flag being off
    // pass through unchanged. Intensity is handled above; this covers the custom
    // metric columns KPI benchmarks use.
    const prescribed = benchmarkData
        ? applyBenchmark(prescribedData, {
            metricType: type,
            metricId: benchmarkData.metricId,
            benchmarkGridId: benchmarkData.benchmarkGridId,
            libraryExerciseId: benchmarkData.libraryExerciseId,
        }, benchmarkData.applyBenchmarkData, benchmarkData.profileId)
        : prescribedData;
    const actuals = benchmarkData
        ? applyBenchmark(actualsData, {
            metricType: type,
            metricId: benchmarkData.metricId,
            benchmarkGridId: benchmarkData.benchmarkGridId,
            libraryExerciseId: benchmarkData.libraryExerciseId,
        }, benchmarkData.applyBenchmarkData, benchmarkData.profileId)
        : actualsData;
    // A `%` prescription that stayed a percentage (the column is not KPI-mapped, or
    // the athlete has no recorded benchmark) is rendered the way the intensity
    // branch above renders an unresolved `%`: the `%` travels with the number in
    // the placeholder, and the unit is left unset because `%` is not a collectable
    // unit. The two are separate UI elements on tablet (a placeholder plus a unit
    // picker that excludes `%`), so returning `unit: '%'` there left a bare number
    // beside an empty picker instead of "123%".
    const isPercentValue = (data) => typeof data === 'object' && 'unit' in data && data.unit === '%';
    const prescribedIsPercent = isPercentValue(prescribed);
    let value = getValueFromData(actuals ?? prescribed);
    const prescribedValueText = getValueFromData(prescribed);
    const placeholder = prescribedIsPercent
        ? `${prescribedValueText}%`
        : prescribedValueText;
    if (typeof value === 'string' && !metric.config?.keywords.includes(value)) {
        // If we used the prescribed value above, it might be a range,
        // which we don't default to and instead show a placeholder for
        // except for keywords
        value = '';
    }
    // A `%` never belongs in `value`: it is a prescription, and actuals are never
    // collected in `%`. The merged workout carries the prescription through as the
    // set metric's input when nothing has been recorded yet, so without this the
    // input renders a bare "123" and its own placeholder ("123%") never shows. The
    // intensity branch above draws the same line: only a resolved weight lands in
    // `value`, an unresolved percentage stays in the placeholder.
    if (isPercentValue(actuals ?? prescribed)) {
        value = '';
    }
    const unit = typeof actuals === 'object' && 'unit' in actuals
        ? actuals.unit
        : typeof prescribed === 'object' && 'unit' in prescribed
            ? prescribed.unit
            : undefined;
    return {
        value,
        placeholder,
        unit: unit === '' || unit === '%' ? undefined : unit,
        prescribedParseError,
        actualParseError,
    };
}
//# sourceMappingURL=display.js.map